import {
  ref, onMounted, onUnmounted, watch, toRefs, computed,
} from 'vue';

import { useResizeObserver } from '@vueuse/core';

import useDisplay from './useDisplay';
import useFacets from './useFacets';
import { useSortFilterSelections } from './useSortFilter';
import usePreventAnimation from './usePreventAnimation';
import useOneClickSaleFilterExp from './useOneClickSaleFilterExp';

export default function useFilteringFacets(props, emit) {
  const {
    allSelectedCriteria,
    addFacetOptions,
    addSortByOptions,
    subscribeFacetsUpdate,
    setFacetTypes,
    setGlobalOptions,
    setSlideoutFacets,
    setLoadingStatus,
    globalOptions,
  } = useFacets();

  const { setPreventAnimation } = usePreventAnimation();
  const { updateDisplay, setDisplayModes, isDropdownMode } = useDisplay();

  const { setSortFilterSelections } = useSortFilterSelections();
  const { oneClickSaleExpEnabled } = useOneClickSaleFilterExp();

  const filteringFacetsGrid = ref(null);
  const {
    showFacetSelectedValuesList, maxCustomRangeValueLength, prioritizedFacets, drawerOptions,
    slideoutFacets, maxDesktopFilters, doDynamicFacetCounterUpdateSlideout, productCount,
    totalFacetSelectedCount, mySizesData, disableDeliveryAndFulfillmentFacet, maxTabletFiltersNumber,
    consumerStore, pageType, xapiExperimentIds, deviceInfo, isSYS, isDynamicFilters, isProductsLoading,
  } = toRefs(props);

  const suppressAndOrderFacets = (arr) => {
    const suppressedFacets = {
      SPECIAL_OFFERS: oneClickSaleExpEnabled.value,
      PRICE_DISCOUNT_RANGE: true,
    };

    const trailingOrder = ['SPECIAL_OFFERS', 'PRICE_DISCOUNT_RANGE'];

    const trailingMap = new Map();

    const mainFacets = arr.reduce((acc, facet) => {
      if (suppressedFacets[facet?.displayName]) {
        trailingMap.set(facet?.displayName, facet);
      } else {
        acc.push(facet);
      }
      return acc;
    }, []);

    const trailingFacets = trailingOrder.reduce((acc, displayName) => {
      if (trailingMap.has(displayName)) {
        acc.push(trailingMap.get(displayName));
        trailingMap.delete(displayName);
      }
      return acc;
    }, []);

    return [...mainFacets, ...trailingFacets];
  };

  const sortedFacets = suppressAndOrderFacets(props.facets);

  watch(() => props.facets, (newVal) => {
    const sortedVal = suppressAndOrderFacets(newVal);
    addFacetOptions(sortedVal);
  });

  watch(() => props.displayModes, (newVal) => {
    setDisplayModes(newVal);
  });

  watch(() => props.slideoutFacets, (val) => {
    if (val?.facets) {
      val.facets = suppressAndOrderFacets(val.facets);
    }
    setSlideoutFacets(val ? { ...val } : null);
  });

  const onSelectedOption = (selectedFacets, isClearAll) => {
    /* istanbul ignore else */
    if (props.doDynamicFacetCounterUpdateSlideout) {
      setLoadingStatus(true); // showing the spinner when emitting an event.
      emit('selectedOptions', {
        selectedFacets, isClearAll,
      });
    }
  };

  const onSaveMySizesToggle = (payload, checked) => {
    emit('saveMySizesToggle', payload, checked);
  };

  addFacetOptions(sortedFacets);
  setDisplayModes(props.displayModes);
  // Sortby on MEW
  watch(() => props.sortBy, () => {
    addSortByOptions(props.sortBy);
  }, { immediate: true });

  setFacetTypes(props.customFacetTypes);
  setGlobalOptions({
    showFacetSelectedValuesList,
    maxCustomRangeValueLength,
    prioritizedFacets,
    drawerOptions,
    onSelectedOption,
    maxDesktopFilters,
    maxTabletFiltersNumber,
    productCount,
    totalFacetSelectedCount,
    doDynamicFacetCounterUpdateSlideout,
    disableDeliveryAndFulfillmentFacet,
    mySizesData,
    onSaveMySizesToggle,
    isMounted: ref(false),
    consumerStore,
    pageType,
    xapiExperimentIds,
    deviceInfo,
    isSYS,
    isDynamicFilters,
    isProductsLoading,
  });

  setSlideoutFacets(slideoutFacets.value ? { ...slideoutFacets.value } : null);

  // update selections when new facet data is loaded
  watch(slideoutFacets, (Val) => {
    setPreventAnimation(true);
    setTimeout(() => { setPreventAnimation(false); }, 700);

    setSortFilterSelections(Val?.facets);
    setLoadingStatus(false); // removing the spinner after response is received.
  });

  let unsubscribe;

  onMounted(() => {
    unsubscribe = subscribeFacetsUpdate(() => {
      emit('selectedCriteria', {
        allSelected: allSelectedCriteria.value,
      });
    });

    useResizeObserver(filteringFacetsGrid, () => {
      updateDisplay();
    });
    globalOptions.isMounted.value = true;
  });

  onUnmounted(() => {
    /* istanbul ignore else */
    if (unsubscribe) {
      unsubscribe();
    }
  });

  const rootCssClass = computed(() => [{ sticky: false }, 'facets-optimized']);

  return {
    filteringFacetsGrid,
    onSelectedOption,
    isDropdownMode,
    slideoutFacets,
    setLoadingStatus,
    onSaveMySizesToggle,
    rootCssClass,
    sortedFacets,
  };
}
